import { type ReactNode, useMemo } from 'react';

import { cn, ConditionalRender, Skeleton } from '@crossnokaye/ux-common';

import { IconConfig, IconConfigVariant } from '@repo/sdk/models/components';

import { AnimaticIcon } from '@/features/equipment-browser/animatics/animatic-icon';
import { Stack } from '@/ux-common/components/stack/stack';

import { chunkShelfIconsIntoRows } from './animatic-shelf.layout';

const getShelfIconSlotKey = (icon: IconConfig, index: number) =>
  `${icon.icon}-${icon.label ?? ''}-${index}`;

type ShelfIconSectionProps = {
  icon: IconConfig;
  isMobile: boolean;
};

const ShelfIconSection = ({ icon, isMobile }: ShelfIconSectionProps) => {
  const iconVariant = icon.variant ?? IconConfigVariant.Default;
  const label = icon.label;
  return (
    <Stack
      data-component="ShelfIconSection"
      className="stack-thin w-full items-center p-2 @2xl:p-1"
    >
      <AnimaticIcon icon={icon.icon} variant={iconVariant} size={isMobile ? 'sm' : 'md'} />
      <ConditionalRender condition={!!label}>
        <span
          className={cn('font-bold truncate', isMobile ? 'text-xs leading-4' : 'text-sm leading-5')}
        >
          {label}
        </span>
      </ConditionalRender>
    </Stack>
  );
};

type ShelfIconRowProps = {
  icons: IconConfig[];
  isMobile: boolean;
  showTopBorder: boolean;
};

const ShelfIconRow = ({ icons, isMobile, showTopBorder }: ShelfIconRowProps) => (
  <div
    data-component="ShelfIconRow"
    className={cn('flex w-full', showTopBorder && 'border-t-2 border-tertiary')}
  >
    {icons.map((icon, index) => (
      <div
        key={getShelfIconSlotKey(icon, index)}
        data-component="ShelfIconSlot"
        className={cn('min-w-0 flex-1', index < icons.length - 1 && 'border-r-2 border-tertiary')}
      >
        <ShelfIconSection icon={icon} isMobile={isMobile} />
      </div>
    ))}
  </div>
);

type AnimaticShelfIconsProps = {
  icons: IconConfig[];
  isMobile: boolean;
};

export const AnimaticShelfIcons = ({ icons, isMobile }: AnimaticShelfIconsProps): ReactNode => {
  const rows = useMemo(() => chunkShelfIconsIntoRows(icons), [icons]);

  if (icons.length === 0) {
    return null;
  }

  return (
    <div
      data-component="AnimaticShelfIcons"
      className="w-full overflow-hidden rounded-md border-2 border-tertiary"
    >
      {rows.map((rowIcons, rowIndex) => (
        <ShelfIconRow
          key={`shelf-icon-row-${rowIcons.map((icon) => icon.icon).join('-')}-${rowIndex}`}
          icons={rowIcons}
          isMobile={isMobile}
          showTopBorder={rowIndex > 0}
        />
      ))}
    </div>
  );
};

export const IconShelfSkeletons = () => {
  return (
    <Stack data-component="IconShelfSkeletons" className="stack-sm w-full">
      <Skeleton className="w-40 h-4" />
      <Skeleton className="w-full h-10" />
    </Stack>
  );
};
